import type {
  CampaignBannerRecipeBannerTagGroup,
  CampaignBannerRecipeTagGroups,
  CompactCampaignBannerRecipeTagGroups,
  MenuRecipeDietaryTag,
  MenuRecipeGeneralTag,
  MenuRecipeSubtitleTag,
} from '@library/api-menu-experience'
import type { Recipe } from '@library/menu-service'

const SOLD_OUT_COLORS = {
  color: {
    light: '#FFFFFF',
    dark: '#FFFFFF',
  },
  backgroundColor: {
    light: '#333D47',
    dark: '#333D47',
  },
} as const

const INVALID_PREFERENCE_COLORS = {
  color: {
    light: '#FFFFFF',
    dark: '#FFFFFF',
  },
  backgroundColor: {
    light: '#424F5C',
    dark: '#424F5C',
  },
} as const

export const R2H_COOKED_BY_US_TAG = { type: 'cookedByUs' as const, text: 'Cooked by us' }
const R2H_NO_NASTIES_TAG = { type: 'noNasties' as const, text: 'No nasties' }

const R2H_BANNER_COLORS = {
  color: {
    light: '#ffffff',
    dark: '#ffffff',
  },
  backgroundColor: {
    light: '#BB4213',
    dark: '#BB4213',
  },
}

const R2H_BANNER_TAG_GROUP: CampaignBannerRecipeBannerTagGroup = {
  tags: [R2H_COOKED_BY_US_TAG, R2H_NO_NASTIES_TAG],
  colors: R2H_BANNER_COLORS,
}

export const COMPACT_R2H_BANNER_TAG_GROUP: CampaignBannerRecipeBannerTagGroup = {
  tags: [R2H_COOKED_BY_US_TAG],
  colors: R2H_BANNER_COLORS,
}

const DIETARY_TAGS = [
  {
    slug: 'vegan',
    tag: {
      type: 'plantBased' as const,
      text: 'PB',
      accessibleText: 'Plant-based',
    },
  },
  {
    slug: 'vegetarian',
    tag: {
      type: 'vegetarian' as const,
      text: 'V',
      accessibleText: 'Vegetarian',
    },
  },
  {
    slug: 'dairy-free',
    tag: {
      type: 'dairyFree' as const,
      text: 'DF',
      accessibleText: 'Dairy-free',
    },
  },
  {
    slug: 'gluten-free',
    tag: {
      type: 'glutenFree' as const,
      text: 'GF',
      accessibleText: 'Gluten-free',
    },
  },
] as const

const formatGrams = (milligrams: number) => `${(milligrams / 1000).toFixed(1)}g`

const formatSubtitleTag = (
  type: MenuRecipeSubtitleTag['type'],
  text: string,
  accessibleText = text,
): MenuRecipeSubtitleTag => ({
  type,
  text,
  accessibleText,
})

const toDietaryTags = (recipe: Recipe): MenuRecipeDietaryTag[] =>
  DIETARY_TAGS.flatMap(({ slug, tag }) =>
    recipe.dietaryClaims.some((claim) => claim.slug === slug) ? [tag] : [],
  )

const toGeneralTags = (recipe: Recipe): MenuRecipeGeneralTag[] => {
  if (!recipe.isAvailable) {
    return [
      {
        type: 'soldOut',
        text: 'Sold Out',
        ...SOLD_OUT_COLORS,
      },
    ]
  }

  if (recipe.invalidPreference) {
    return [
      {
        type: 'invalidPreference',
        text: recipe.invalidPreference,
        ...INVALID_PREFERENCE_COLORS,
      },
    ]
  }

  const tags: MenuRecipeGeneralTag[] = []

  if (recipe.merchandisingTags.availability) {
    tags.push({
      type: 'generic',
      text: recipe.merchandisingTags.availability.name,
      color: recipe.merchandisingTags.availability.color,
      backgroundColor: recipe.merchandisingTags.availability.backgroundColor,
    })
  }

  if (recipe.merchandisingTags.tagline) {
    tags.push({
      type: 'generic',
      text: recipe.merchandisingTags.tagline.name,
      color: recipe.merchandisingTags.tagline.color,
      backgroundColor: recipe.merchandisingTags.tagline.backgroundColor,
    })
  }

  return tags
}

const toSpiceTag = (recipe: Recipe): MenuRecipeSubtitleTag => {
  switch (recipe.spiceLevel?.name?.toLowerCase()) {
    case 'mild':
      return formatSubtitleTag('spiceMild', 'Mild')
    case 'medium':
      return formatSubtitleTag('spiceMedium', 'Medium')
    case 'hot':
      return formatSubtitleTag('spiceHot', 'Hot')
    default:
      return formatSubtitleTag('spiceNone', 'Not spicy')
  }
}

const toSubtitleTags = (recipe: Recipe): MenuRecipeSubtitleTag[] => {
  const subtitleTags: MenuRecipeSubtitleTag[] = []

  if (recipe.prepTime !== null) {
    subtitleTags.push(
      formatSubtitleTag('cookingTime', `${recipe.prepTime} min`, `${recipe.prepTime} minutes`),
    )
  }

  subtitleTags.push(
    formatSubtitleTag(
      'energy',
      `${recipe.nutritionalInformation.perPortion.energyKcal} kcal`,
      `${recipe.nutritionalInformation.perPortion.energyKcal} kilocalories`,
    ),
  )

  subtitleTags.push(
    formatSubtitleTag(
      'protein',
      `${formatGrams(recipe.nutritionalInformation.perPortion.proteinMg)} protein`,
    ),
  )

  if (recipe.fiveADay && recipe.fiveADay > 0) {
    subtitleTags.push(formatSubtitleTag('fiveADay', `${recipe.fiveADay} of 5 a day`))
  }

  subtitleTags.push(formatSubtitleTag('rating', recipe.rating.average.toFixed(1)))

  if (recipe.shelfLife) {
    subtitleTags.push(
      formatSubtitleTag(
        'shelfLife',
        `Use within ${recipe.shelfLife.minDays}-${recipe.shelfLife.maxDays} days`,
      ),
    )
  }

  subtitleTags.push(toSpiceTag(recipe))

  subtitleTags.push(
    formatSubtitleTag('fat', `${formatGrams(recipe.nutritionalInformation.perPortion.fatMg)} fat`),
  )
  subtitleTags.push(
    formatSubtitleTag(
      'carbs',
      `${formatGrams(recipe.nutritionalInformation.perPortion.carbsMg)} carbs`,
    ),
  )
  subtitleTags.push(
    formatSubtitleTag(
      'salt',
      `${formatGrams(recipe.nutritionalInformation.perPortion.saltMg)} salt`,
    ),
  )

  return subtitleTags
}

export const toCampaignBannerRecipeTagGroups = (recipe: Recipe): CampaignBannerRecipeTagGroups => {
  const displayType = recipe.preparationType === 'heat-only' ? 'ready-to-heat' : 'standard'

  return {
    dietary: toDietaryTags(recipe),
    general: toGeneralTags(recipe),
    subtitle: toSubtitleTags(recipe),
    ...(displayType === 'ready-to-heat' ? { banner: R2H_BANNER_TAG_GROUP } : {}),
  }
}

export const toCompactCampaignBannerRecipeTagGroups = (
  recipe: Recipe,
): CompactCampaignBannerRecipeTagGroups | undefined => {
  if (recipe.preparationType !== 'heat-only') {
    return undefined
  }

  return {
    banner: COMPACT_R2H_BANNER_TAG_GROUP,
  }
}
